export interface ParsedSubmissionEvidence {
  description: string;
  imageUrls: string[];
  legacyImagePaths: string[];
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

export function canPreviewImage(uri: string): boolean {
  return /^data:image\//i.test(uri) || /^https?:\/\//i.test(uri);
}

export function parseSubmissionEvidence(raw: string): ParsedSubmissionEvidence {
  const trimmed = raw.trim();
  if (!trimmed) return { description: '', imageUrls: [], legacyImagePaths: [] };

  try {
    const parsed = JSON.parse(trimmed) as unknown;
    if (isObject(parsed)) {
      const imageCandidates = [
        ...stringArray(parsed.image_data_urls),
        ...stringArray(parsed.image_urls),
        ...stringArray(parsed.images),
      ].map((item) => item.trim()).filter(Boolean);
      return {
        description: typeof parsed.description === 'string' ? parsed.description.trim() : '',
        imageUrls: imageCandidates.filter(canPreviewImage),
        legacyImagePaths: imageCandidates.filter((item) => !canPreviewImage(item)),
      };
    }
  } catch {
    // Fall through to legacy plain-text evidence parsing.
  }

  const lines = trimmed.split('\n').map((line) => line.trim()).filter(Boolean);
  const imageLines = lines.filter(canPreviewImage);
  const legacyImagePaths = lines.filter((line) => /^file:\/\//i.test(line));
  const descriptionLines = lines
    .filter((line) => !canPreviewImage(line) && !/^file:\/\//i.test(line));
  const description =
    descriptionLines.length > 0 || imageLines.length > 0 || legacyImagePaths.length > 0
      ? descriptionLines.join('\n')
      : trimmed;
  return { description, imageUrls: imageLines, legacyImagePaths };
}
